import { useInfiniteQuery } from "@tanstack/react-query";
import { useDeferredValue, useState } from "react";

import type { SupportTicketSummary, SupportViewKey } from "../../shared/support-schemas";
import { ContentLoading } from "../components/ContentLoading";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { FieldError } from "../components/ui/field";
import { Input } from "../components/ui/input";
import { RowTable, type RowColumn } from "../components/ui/row-table";
import type { OperationsNavigate } from "./navigation";
import { cn } from "../lib/utils";
import { listSupportTickets, LIVE_LIST, supportKeys } from "./support-api";
import {
  CHANNEL_LABELS,
  formatWhen,
  PRIORITY_BADGES,
  PRIORITY_LABELS,
  replyDue,
  requesterLabel,
  STATUS_BADGES,
  STATUS_LABELS,
  VIEW_LABELS,
} from "./support-format";
import { supportTicketPath } from "./support-route";
import { SupportBulkBar } from "./SupportBulkBar";

const COLUMNS: readonly RowColumn<SupportTicketSummary>[] = [
  {
    key: "subject",
    header: "Ticket",
    cell: (ticket) => (
      <span className="flex min-w-48 flex-col gap-0.5">
        <span className="font-semibold">{ticket.subject}</span>
        <span className="text-sm text-muted-foreground">
          {ticket.reference} · {CHANNEL_LABELS[ticket.channel]}
          {ticket.hasDraft ? " · Draft ready" : ""}
        </span>
      </span>
    ),
  },
  {
    key: "status",
    header: "Status",
    cell: (ticket) => <StatusCell ticket={ticket} />,
  },
  {
    key: "customer",
    header: "Customer",
    className: "hidden md:table-cell",
    cell: (ticket) => (
      <span className="flex flex-col gap-0.5">
        <span>{ticket.customerName}</span>
        <span className="text-sm text-muted-foreground">{requesterLabel(ticket.requester)}</span>
      </span>
    ),
  },
  {
    key: "priority",
    header: "Priority",
    className: "hidden xl:table-cell",
    cell: (ticket) => (
      <Badge variant={PRIORITY_BADGES[ticket.priority]}>{PRIORITY_LABELS[ticket.priority]}</Badge>
    ),
  },
  {
    key: "assignee",
    header: "Assignee",
    className: "hidden 2xl:table-cell",
    cell: (ticket) => ticket.assignee?.displayName ?? "Unassigned",
  },
  {
    key: "updated",
    header: "Updated",
    className: "hidden 2xl:table-cell",
    cell: (ticket) => (
      <span className="text-sm text-muted-foreground">{formatWhen(ticket.updatedAt)}</span>
    ),
  },
];

function StatusCell({ ticket }: { ticket: SupportTicketSummary }) {
  const due = replyDue(ticket.replyDueAt);
  return (
    <span className="flex flex-col items-start gap-1">
      <Badge variant={STATUS_BADGES[ticket.status]}>{STATUS_LABELS[ticket.status]}</Badge>
      {due ? (
        <span
          className={cn(
            "text-sm whitespace-nowrap",
            due.overdue ? "font-semibold text-destructive" : "text-muted-foreground",
          )}
        >
          {due.text}
        </span>
      ) : null}
    </span>
  );
}

function QueueHeader({
  view,
  search,
  onSearch,
}: {
  view: SupportViewKey;
  search: string;
  onSearch: (search: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <h2 id="support-queue-title" className="text-xl font-bold">
        {VIEW_LABELS[view]}
      </h2>
      <label className="w-full max-w-xs" htmlFor="support-search">
        <span className="sr-only">Search tickets</span>
        <Input
          id="support-search"
          type="search"
          value={search}
          placeholder="Search tickets, messages, customers, or #number"
          onChange={(event) => {
            onSearch(event.target.value);
          }}
        />
      </label>
    </div>
  );
}

function useQueue(view: SupportViewKey, search: string) {
  return useInfiniteQuery({
    queryKey: supportKeys.list(view, search),
    queryFn: ({ pageParam }) => listSupportTickets({ view, search, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
    ...LIVE_LIST,
  });
}

/** One view's tickets, newest activity first, with search and "Show more" paging. */
export function SupportTicketQueue({
  view,
  navigate,
  canWrite = false,
}: {
  view: SupportViewKey;
  navigate: OperationsNavigate;
  /** Offers checkboxes and one change to many tickets. */
  canWrite?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const deferredSearch = useDeferredValue(search.trim());
  const tickets = useQueue(view, deferredSearch);
  const rows = tickets.data?.pages.flatMap((page) => page.tickets) ?? [];
  const selected = rows.filter((ticket) => checked.has(ticket.id)).map((ticket) => ticket.id);
  return (
    <section aria-labelledby="support-queue-title" className="flex flex-col gap-4">
      <QueueHeader view={view} search={search} onSearch={setSearch} />
      {selected.length > 0 ? (
        <SupportBulkBar
          ticketIds={selected}
          onClear={() => {
            setChecked(new Set());
          }}
        />
      ) : null}
      {tickets.isPending ? <ContentLoading label="Loading tickets…" variant="rows" /> : null}
      {tickets.error ? <FieldError>{tickets.error.message}</FieldError> : null}
      {tickets.isSuccess ? (
        <RowTable
          label={VIEW_LABELS[view]}
          columns={COLUMNS}
          rows={rows}
          rowKey={(ticket) => ticket.id}
          rowLabel={(ticket) => `Open ${ticket.reference}: ${ticket.subject}`}
          onOpen={(ticket) => {
            navigate(supportTicketPath(ticket.id));
          }}
          empty={deferredSearch ? "No tickets match that search." : "No tickets in this view."}
          checks={
            canWrite
              ? { checked, onChange: setChecked, label: (ticket) => `Select ${ticket.reference}` }
              : undefined
          }
        />
      ) : null}
      {tickets.hasNextPage ? (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={tickets.isFetchingNextPage}
            onClick={() => {
              void tickets.fetchNextPage();
            }}
          >
            {tickets.isFetchingNextPage ? "Loading…" : "Show more"}
          </Button>
        </div>
      ) : null}
    </section>
  );
}
